import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Target, Leaf, Users, ArrowRight, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNavigate, Link } from 'react-router-dom';
import { geminiService } from '../services/geminiService';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import toast from 'react-hot-toast';
import { loggingService } from '../services/loggingService';
import { monitoringService } from '../services/monitoringService';
import { validateCareerAnalysis } from '../types/analysis';
import { useAnalysisStore } from '../stores/analysisStore';
import { useDashboardStore } from '../stores/dashboardStore';

export default function InteractiveQuiz({ onShowAuth }: { onShowAuth: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plan, quizGenerationsLeft, decrementQuizGenerations } = useSubscriptionStore();
  const { setAnalysis, saveAnalysisToDatabase } = useAnalysisStore();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [interests, setInterests] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Rozpoczynanie...');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [hasRedirected, setHasRedirected] = useState(false);
  
  // Sprawdź czy użytkownik już wykonał quiz i czy faktycznie ma wygenerowaną analizę
  const existingQuiz = user ? localStorage.getItem(`quiz_${user.id}`) : null;
  const existingAnalysis = user ? localStorage.getItem(`analysis_${user.id}`) : null;
  const analysisGenerated = user ? localStorage.getItem(`analysis_generated_${user.id}`) === "true" : false;
  
  const hasUsedFreeGeneration = existingQuiz && plan === 'free' && analysisGenerated;
  const canGenerateNewAnalysis = plan === 'premium' && quizGenerationsLeft > 0;
  
  // Zmodyfikowana logika - pokazuj banner tylko jeśli faktycznie istnieje analiza,
  // a nie tylko wypełniony quiz (który mógł się nie powieść)
  const showQuizCompletedBanner = existingQuiz && analysisGenerated && !canGenerateNewAnalysis;
  
  // Resetuj stan przekierowania przy montowaniu komponentu
  useEffect(() => {
    setHasRedirected(false);
  }, []);

  // Funkcja do czyszczenia interwału
  const clearProgressInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Wyczyszczenie interwału przy odmontowaniu komponentu
  useEffect(() => {
    return () => {
      clearProgressInterval();
    };
  }, []);

  const handleOptionSelect = (value: string) => {
    if (showQuizCompletedBanner) {
      if (canGenerateNewAnalysis) {
        toast.error('Musisz najpierw zresetować poprzednią analizę w dashboardzie');
      } else {
        toast('Wykup plan Premium aby wygenerować nową analizę', {
          icon: '✨',
          duration: 4000
        });
      }
      return;
    }

    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter(v => v !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
    }
  };

  const handleNextStep = () => {
    if (showQuizCompletedBanner) {
      if (canGenerateNewAnalysis) {
        toast.error('Musisz najpierw zresetować poprzednią analizę w dashboardzie');
      } else {
        toast('Wykup plan Premium aby wygenerować nową analizę', {
          icon: '✨',
          duration: 4000
        });
      }
      return;
    }

    if (currentStep === 1 && selectedOptions.length === 0) {
      toast.error('Wybierz co najmniej jedną opcję');
      return;
    }
    if (currentStep === 2 && !interests.trim()) {
      toast.error('Opisz swoje zainteresowania');
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // === LOGI STARTOWE ===
    console.log('[QuizSubmit] 0. handleSubmit TRIGGERED');
    if (e && e.preventDefault) {
      console.log('[QuizSubmit] 1. Preventing default event');
      e.preventDefault();
      } else {
      console.log('[QuizSubmit] 1. Event object missing preventDefault');
    }

    console.log('[QuizSubmit] 2. Checking user object:', user);
    if (!user) {
      console.error('[QuizSubmit] 2a. USER IS NULL - Błąd krytyczny!');
      toast.error('Błąd: Brak danych użytkownika. Spróbuj się zalogować ponownie.');
      onShowAuth();
      return; // ZATRZYMANIE
    }
    console.log(`[QuizSubmit] 2b. User OK: ${user.id}`);

    console.log('[QuizSubmit] 3. Checking selected options:', selectedOptions);
    if (selectedOptions.length === 0) {
      console.warn('[QuizSubmit] 3a. No options selected.');
      toast.error('Wybierz przynajmniej jedną opcję!');
      return; // ZATRZYMANIE
    }
    console.log('[QuizSubmit] 3b. Options OK:', selectedOptions);

    console.log('[QuizSubmit] 4. Checking plan and generations left:', { plan, quizGenerationsLeft });
    if (plan === 'free' && quizGenerationsLeft <= 0) {
      console.warn('[QuizSubmit] 4a. Free plan limit reached.');
      toast.error('Wykorzystałeś limit darmowych generacji.');
      navigate('/dashboard'); // Lub /plany
      return; // ZATRZYMANIE
    }
    console.log('[QuizSubmit] 4b. Plan/Generations OK.');
    // === KONIEC LOGÓW STARTOWYCH ===

    console.log('[QuizSubmit] 5. Passed initial checks. Setting isSubmitting=true');
    setIsSubmitting(true);
    setProgress(5);
    setProgressText('Przygotowuję zapytanie...');
    const loadingToastId = 'analysis-loading';
    toast.loading('Generowanie analizy kariery...', { id: loadingToastId });

    // Uruchomienie symulowanego postępu
    clearProgressInterval(); // Wyczyść poprzedni interwał, jeśli istnieje
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        const nextProgress = prev + Math.floor(Math.random() * 5) + 1; // Losowy mały przyrost
        if (nextProgress >= 90) {
          clearProgressInterval(); // Zatrzymaj na 90%
          return 90;
        }
        return nextProgress;
      });
      setProgressText('Przetwarzanie przez AI...'); // Aktualizuj tekst
    }, 800); // Aktualizacja co 800ms

    let quizDataString = '';
    const quizStorageKey = `quiz_${user.id}`;

    try {
      console.log('[QuizSubmit] 7. Entering TRY block.');
      const quizData = {
        user_id: user.id,
        preferences: selectedOptions,
        additional_interests: interests.trim(),
        additional_info: additionalInfo.trim(),
        plan_type: plan,
        date: new Date().toISOString()
      };
      quizDataString = JSON.stringify(quizData);
      console.log('[QuizSubmit] 8. Quiz data prepared:', quizData);

      console.log(`[QuizSubmit] 9. Saving quizData to localStorage (Key: ${quizStorageKey})`);
      localStorage.setItem(quizStorageKey, quizDataString);
      if (localStorage.getItem(quizStorageKey) === quizDataString) {
        console.log('[QuizSubmit] 9a. localStorage quiz save confirmed.');
      } else {
        console.error('[QuizSubmit] 9b. ERROR: Failed to save quizData to localStorage!');
      }

      loggingService.info('Rozpoczęto generowanie analizy kariery', { userId: user.id });

      // Przygotowanie danych wejściowych dla generateCareerAnalysis
      const analysisInput = {
        preferences: selectedOptions,
        additional_interests: interests.trim(),
        additional_info: additionalInfo.trim()
      };
      console.log('[QuizSubmit] 11. Prepared input for generateCareerAnalysis:', analysisInput);

      let rawResponse: any;
      try {
        setProgressText('Komunikacja z AI...'); // Aktualizacja tekstu
        rawResponse = await geminiService.generateCareerAnalysis(analysisInput);
        clearProgressInterval(); // Zatrzymaj symulowany postęp po otrzymaniu odpowiedzi
        setProgress(92); // Skok po odpowiedzi
        setProgressText('Przetwarzam odpowiedź AI...');
        console.log("[QuizSubmit] 12a. Received response from Gemini API (raw):", rawResponse);
      } catch (geminiError: any) {
        console.error("[QuizSubmit] === GEMINI API CRITICAL ERROR ===");
        console.error("Error Name:", geminiError?.name);
        console.error("Error Message:", geminiError?.message);
        console.error("Error Stack:", geminiError?.stack);
        console.error("Full Error Object:", geminiError);
        loggingService.error('Critical Gemini API call error', {
           errorName: geminiError?.name,
           errorMessage: geminiError?.message,
           errorStack: geminiError?.stack,
           userId: user.id
         });
        throw new Error(`Błąd AI: ${geminiError?.message || 'Unknown Gemini error'}`); // Re-throw
      }

      if (!rawResponse) {
        console.error('[QuizSubmit] 14. Empty response from Gemini API.');
        throw new Error('Otrzymano pustą odpowiedź z AI. Spróbuj ponownie.');
      }

      let analysis: any;
      console.log('[QuizSubmit] 15. Parsing Gemini response...');
      try {
        setProgress(95);
        setProgressText('Analizuję strukturę...');
        if (typeof rawResponse === 'string') {
          const cleanedResponse = rawResponse.replace(/```json/g, '').replace(/```/g, '').trim();
          analysis = JSON.parse(cleanedResponse);
        } else {
          analysis = rawResponse; // Assume it's already an object
        }
        console.log("[QuizSubmit] 15a. Successfully parsed analysis:", analysis);
      } catch (parseError: any) {
         console.error("[QuizSubmit] 15b. ERROR parsing Gemini response:", parseError, "Original response:", rawResponse);
         localStorage.setItem(`debug_gemini_raw_${user.id}`, typeof rawResponse === 'string' ? rawResponse : JSON.stringify(rawResponse));
         throw new Error(`Niepoprawny format odpowiedzi z AI: ${parseError?.message || 'Błąd parsowania'}`);
      }

      console.log('[QuizSubmit] 16. Validating analysis structure...');
      const isValid = validateCareerAnalysis(analysis);
      if (!isValid) {
        console.error("[QuizSubmit] 16a. ERROR: Analysis validation failed:", analysis);
        localStorage.setItem(`debug_gemini_invalid_${user.id}`, JSON.stringify(analysis));
        throw new Error("Odpowiedź AI nie jest poprawna strukturalnie. Spróbuj ponownie.");
      }
      console.log('[QuizSubmit] 16b. Analysis structure OK.');

      const analysisString = JSON.stringify(analysis);
      const analysisStorageKey = `analysis_${user.id}`;

      console.log(`[QuizSubmit] 17. Saving analysis to localStorage (Key: ${analysisStorageKey})`);
      localStorage.setItem(analysisStorageKey, analysisString);
      if (localStorage.getItem(analysisStorageKey) === analysisString) {
         console.log('[QuizSubmit] 17a. localStorage analysis save confirmed.');
      } else {
         console.error('[QuizSubmit] 17b. ERROR: Failed to save analysis to localStorage!');
      }

      console.log("[QuizSubmit] 18. Setting analysis in global store...");
      setAnalysis(analysis);
      console.log('[QuizSubmit] 18a. Analysis set in store.');

      localStorage.setItem(`analysis_generated_${user.id}`, "true");
      console.log('[QuizSubmit] 19. Set analysis_generated flag.');

      if (plan === 'free') {
        decrementQuizGenerations();
        console.log('[QuizSubmit] 20. Decremented free plan generations.');
      }

      // Zapis do DB w tle (bez await)
      saveAnalysisToDatabase(user.id).catch(dbError => {
        console.error("[QuizSubmit] ERROR saving analysis to DB (background):", dbError);
        loggingService.error('Błąd zapisu analizy do DB w tle', { userId: user.id, error: dbError });
      });

      // Aktualizuj postęp w dashboardStore
      try {
        await useDashboardStore.getState().updateProgress({ quizCompleted: true, analysisGenerated: true, profileCompleted: 66 });
        console.log('[QuizSubmit] Zaktualizowano postęp w dashboardStore.');
      } catch (progressError) {
         console.error('[QuizSubmit] Błąd podczas aktualizacji postępu w dashboardStore:', progressError);
         loggingService.error('Błąd aktualizacji postępu dashboard', { userId: user.id, error: progressError });
      }

      // Usunięcie części logów
      console.log('[QuizSubmit] Analiza wygenerowana i zapisana lokalnie.');
      loggingService.info('Wygenerowano analizę kariery', { userId: user.id });
      toast.success('Analiza kariery wygenerowana pomyślnie!', { id: loadingToastId });

      setProgress(100);
      setProgressText('Przekierowuję...');
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/dashboard');

    } catch (error: any) {
      clearProgressInterval(); // Upewnij się, że interwał jest wyczyszczony przy błędzie
      console.error('[QuizSubmit] === MAIN TRY BLOCK ERROR ===', error);
      loggingService.error('Błąd generowania analizy (główny catch)', { error: error?.message || 'Unknown error', userId: user?.id });
      toast.error(error?.message || 'Wystąpił błąd podczas generowania analizy.', { id: loadingToastId });

      // Clean up potentially corrupted localStorage items on error - DODAJEMY quiz_${user.id}
      console.log('[QuizSubmit] Cleaning up localStorage after error...');
      localStorage.removeItem(`quiz_${user.id}`); // Dodane usuwanie quizu, żeby umożliwić ponowne próby
      localStorage.removeItem(`analysis_${user.id}`);
      localStorage.removeItem(`analysis_generated_${user.id}`);
      localStorage.removeItem(`debug_gemini_raw_${user.id}`);
      localStorage.removeItem(`debug_gemini_invalid_${user.id}`);
      console.log('[QuizSubmit] localStorage cleanup done.');

    } finally {
      clearProgressInterval(); // Ostateczne wyczyszczenie interwału
      setIsSubmitting(false);
      console.log('[QuizSubmit] Exiting handleSubmit.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Co najbardziej Cię inspiruje?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { value: 'nature', icon: Leaf, text: 'Natura i środowisko' },
                { value: 'ambitious', icon: Target, text: 'Osiąganie celów' },
                { value: 'helping', icon: Users, text: 'Pomaganie innym' },
                { value: 'creative', icon: Brain, text: 'Kreatywne myślenie' }
              ].map((option) => (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOptionSelect(option.value)}
                  className={`w-full flex items-center p-6 border-2 rounded-xl transition-all ${
                    selectedOptions.includes(option.value)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-purple-100 hover:border-purple-300'
                  }`}
                >
                  <option.icon className={`w-8 h-8 mr-4 transition-colors ${
                    selectedOptions.includes(option.value) ? 'text-purple-600' : 'text-purple-400'
                  }`} />
                  <span className="text-lg font-medium text-gray-700">{option.text}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Opowiedz nam o swoich zainteresowaniach
            </h3>
            <textarea
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800 min-h-[150px]"
              placeholder="Np. rozwój osobisty, technologia, analiza danych, zarządzanie..."
            />
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Dodatkowe informacje (opcjonalnie)
            </h3>
            <textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-800 min-h-[150px]"
              placeholder="Opisz swoje dotychczasowe doświadczenia, cele zawodowe lub inne istotne informacje..."
            />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 mt-12" id="quiz">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8"
      >
        {showQuizCompletedBanner && (
          <div className="mb-8 p-4 bg-purple-50 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <Brain className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-purple-900">
                {plan === 'premium' && quizGenerationsLeft > 0
                  ? 'Masz już wygenerowaną analizę'
                  : plan === 'free'
                    ? 'Zużyłeś darmową analizę - wykup Premium dla większej liczby analiz'
                  : 'Wykup plan Premium aby wygenerować nową analizę'}
              </h3>
            </div>
            <p className="text-purple-700 text-sm">
              {plan === 'premium' && quizGenerationsLeft > 0
                ? 'Aby wygenerować nową analizę, przejdź do dashboardu i zresetuj poprzednią.'
                : plan === 'free'
                  ? 'Twoja analiza jest dostępna w dashboardzie. Plan Premium oferuje możliwość wielokrotnego generowania analiz.'
                : 'Plan Premium pozwala na wielokrotne generowanie spersonalizowanych analiz.'}
            </p>
            <div className="mt-4">
              {plan === 'premium' && quizGenerationsLeft > 0 ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  Przejdź do dashboardu
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex gap-3">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors"
                  >
                    Zobacz analizę
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                <Link
                  to="/plany"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  Przejdź do Premium
                  <ArrowRight className="w-4 h-4" />
                </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress steps */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Krok {currentStep} z 3</span>
            <span>{Math.round((currentStep / 3) * 100)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / 3) * 100}%` }}
              className="h-full bg-purple-600"
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        {isSubmitting && (
          <div className="mt-8">
            <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
              <span>{progressText}</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "linear" }}
                className="h-full bg-purple-600"
              />
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8">
          {currentStep > 1 && (
            <button
              onClick={handlePrevStep}
              className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              disabled={isSubmitting}
            >
              Wstecz
            </button>
          )}
          {currentStep < 3 ? (
            <button
              onClick={handleNextStep}
              className="ml-auto flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-purple-700 transition-colors"
              disabled={isSubmitting}
            >
              Dalej
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="ml-auto flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-75 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generowanie analizy...
                </>
              ) : (
                <>
                  Rozpocznij analizę
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
